import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import Util from '@/util';
import { releaseErpTicketPick } from '@/constants/custom/openLink/erpTicketPick';

// modules
import permission from './modules/permission';
import agent from './modules/agent';
import brand from './modules/brand';
import customer from './modules/customer';
import chat from './modules/chat';
import chatbot from './modules/chatbot';
import chatbotTemplate from './modules/chatbotTemplate';
import settings from './modules/settings';
import AI from './modules/inferenceAI';

// socket modules
import customWebSocket from './modules/webSocket';
import callWebsocket from './modules/callWebsocket';
import callStatusSocket from './modules/callWebsocket/status';
import ticketWebSocket from './modules/ticketWebSocket';
import callApiWebsocket from './modules/callApiWebsocket';
import neomeccaCallWebsocket from './modules/callWebsocket/neomeccaCallWebsocket';
import ktCallWebsocket from './modules/callWebsocket/ktCallWebsocket';
import klcnsCallWebsocket from './modules/callWebsocket/klcnsCallWebsocket';
import neoWebRtcWebsocket from './modules/callWebsocket/neoWebRtcWebsocket';

import workplace from './modules/workplace';
import callAgentStatus from './modules/call/agentStatus';
import ticket from './modules/ticket';
import cafe24 from './modules/cafe24';
import callStatus from './modules/call/callStatus';
import talkOperation from './modules/talkOperation';
import callServer from './modules/call/callServer';
import inquiry from './modules/inquiry';
// import agentWebsocket from './modules/agentWebSocket'
import callConsultStatus from './modules/report/callConsultStatus';
import notification from './modules/notification';
import STT from './modules/STT';
import callWebsocketReconnect from './modules/callWebsocket/reconnect';
import webPhone from './modules/call/webPhone';
import agentStatistics from './modules/call/agentStatistics';

import eventBus from '@/EventBus';
import moment from 'moment';

Vue.use(Vuex);

const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
const LOGOUT = 'LOGOUT';
const LOCAL_STORAGE_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const IS_AUTHORIZED = 'isAuthorized';

export const store = new Vuex.Store({
  modules: {
    workplace,
    permission,
    agent,
    brand,
    customer,
    chat,
    chatbot,
    chatbotTemplate,
    settings,
    customWebSocket,
    callWebsocket,
    callStatusSocket,
    ticketWebSocket,
    callApiWebsocket,
    callAgentStatus,
    ticket,
    cafe24,
    callStatus,
    talkOperation,
    callServer,
    inquiry,
    callConsultStatus,
    notification,
    AI,
    callWebsocketReconnect,
    STT,
    neomeccaCallWebsocket,
    ktCallWebsocket,
    klcnsCallWebsocket,
    neoWebRtcWebsocket,
    webPhone,
    agentStatistics
  },

  state: {
    layout: 'default-layout',
    isLoggedIn: !!localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY),
    isConsultDesk: false,
    cgChatNewMessageBadge: 'OFF',
    cgChatStatus: 'OFF'
  },
  getters: {
    layout(state) {
      return state.layout;
    },
    isLoggedIn(state) {
      return state.isLoggedIn;
    },
    isConsultDesk(state) {
      return state.isConsultDesk;
    },
    cgChatStatus(state) {
      return state.cgChatStatus;
    },
    cgChatNewMessageBadge(state) {
      return state.cgChatNewMessageBadge;
    }
  },
  mutations: {
    SET_LAYOUT(state, payload) {
      state.layout = payload + '-layout';
    },
    LOGIN(state) {
      state.pending = true;
    },
    REFRESH(state) {
      state.pending = true;
    },
    LOGIN_SUCCESS(state) {
      state.isLoggedIn = true;
      state.pending = false;
      localStorage.setItem(IS_AUTHORIZED, 'true');
    },
    REFRESH_SUCCESS(state) {
      state.pending = false;
    },
    LOGOUT(state) {
      state.isLoggedIn = false;
      localStorage.setItem(IS_AUTHORIZED, 'false');
    },
    setIsConsultDesk(state, value) {
      state.isConsultDesk = value;
    },
    setCgChatStatus(state, value) {
      state.cgChatStatus = value ? 'ON' : 'OFF';
    },
    setCgChatNewMessageBadge(state, value) {
      state.cgChatNewMessageBadge = value ? 'ON' : 'OFF';
    }
  },
  actions: {
    forceLogin: function ({ commit }, forceLoginParameters) {
      return axios({
        method: 'post',
        url: '/api/v2/external/forceLogout',
        data: forceLoginParameters,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
    },
    setLogin: function ({ commit, dispatch }, responseData) {
      localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, responseData.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, responseData.refreshToken);
      commit('setAgentState', Util.parseLoginToken());
      commit('setCallPort', Util.parseLoginToken().callServerProcessPort || '3025');
      commit(LOGIN_SUCCESS);
      dispatch('setTicketListOption');

      commit('setUserDetails', Util.parseLoginToken());
    },
    setLogout: function ({ commit, dispatch }) {
      // 토큰 삭제 전에 상세 점유를 해제한다. loginExpire 처럼 logout API 없이 setLogout 만 호출하는 경로를 포함한다.
      return releaseErpTicketPick(axios).then(() => {
        commit(LOGOUT);
        // commit(CLEAR_ROLE)
        dispatch('logOutWebPhone');
        dispatch('clearWebPhoneLoginCheck');
        commit('forceCallApiCloseSubscription');
        dispatch('closeCallApiConnect'); // CALL API 웹소켓 disconnect
        commit('forceCloseConnection'); // CG 웹소켓 구독 리스트 해지 및 구독 리스트 초기화
        dispatch('closeWebSocket'); // CG 웹소켓 disconnect
        dispatch('CLOSE_CALL');
        commit('setIsFirstConnect', true, { root: true });
        localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem('agent-authority');
        localStorage.removeItem('selectedType');
        localStorage.removeItem('TICKET_LIST_OPTION');
        localStorage.removeItem('extendAccessTokenInfo');
        localStorage.removeItem('is-show-banner');
        localStorage.removeItem('cartini-key');
        commit('CLEAR_CALL_STATUS');
        commit('CLEAR_CALL_SERVER');
        commit('CLEAR_CALL_DETAILS');
        commit('CLEAR_CALL_AGENT_LIST');
        commit('CLEAR_AGENT');
        commit('CLEAR_CHAT');
        commit('FAIL_CONNECT');
        commit('closeDashboardWin');
        commit('CLOSE_STATUS');
        commit('setCallMode', 'NEOMECCA');
        commit('setMenuPath', null);
        eventBus.$emit('close-group-chat');
      });
    },
    logout: function ({ commit, dispatch }, param) {
      // auth/logout 이 세션을 끊기 전에 unpick 을 보낸다.
      return releaseErpTicketPick(axios)
        .then(() =>
          axios({
            method: 'POST',
            url: '/auth/logout',
            data: { accountId: Util.parseLoginToken() && Util.parseLoginToken().accountId },
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
        )
        .then((res) => {
          return dispatch('setLogout').then(() => {
            commit('setPathActions', []);
            if (param && !param.busOn) {
              Object.keys(eventBus._events).map((d) => {
                if (d !== 'DialogPopup' && d !== 'ClosePopup') {
                  eventBus.$off(d);
                }
              });
            }
            return res;
          });
        });
    },
    forceLogout: function ({ commit, dispatch }) {
      return dispatch('setLogout').then(() => {
        commit('setPathActions', []);
        Object.keys(eventBus._events).map((d) => {
          if (d !== 'DialogPopup' && d !== 'ClosePopup') {
            eventBus.$off(d);
          }
        });
        return true;
      });
    },
    refresh: function ({ commit }) {
      commit('REFRESH');
      return axios({
        method: 'POST',
        url: '/auth/v2/refresh',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((result) => {
          const response = result.data;
          localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, response.token);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
          commit('setAgentState', Util.parseLoginToken());
          commit('setCallPort', Util.parseLoginToken().callServerProcessPort || '3025');
          // commit('setCallServer', Util.parseLoginToken());
          commit('REFRESH_SUCCESS');
          return result;
        })
        .catch(() => {
          commit('REFRESH_SUCCESS');
        });
    },
    setTicketListOption: function () {
      const key = 'TICKET_LIST_OPTION';
      const option = JSON.parse(localStorage.getItem(key));
      if (option) {
        let startDate = moment(new Date(), 'YYYYMMDD').add(-1, 'month').format('YYYY-MM-DD');
        let recentStartDate = moment(new Date(), 'YYYYMMDD').add(-6, 'days').format('YYYY-MM-DD');
        let endDate = moment(new Date(), 'YYYYMMDD').format('YYYY-MM-DD');
        option.myPage.startDate = startDate;
        option.allPage.startDate = startDate;
        option.recentUpdatedPage.startDate = recentStartDate;
        option.completedPage.startDate = startDate;
        option.myPage.endDate = endDate;
        option.allPage.endDate = endDate;
        option.recentUpdatedPage.endDate = endDate;
        option.completedPage.endDate = endDate;
        localStorage.setItem(key, JSON.stringify(option));
      }
    }
  }
});



// WEBPACK FOOTER //
// ./src/store/store.js